import { Injectable } from '@angular/core';
import { BaseRequester } from '../../../../shared/services/common/base-requester';
import { Workbook, Worksheet } from 'exceljs';
import { InventoryItem, InventoryReport } from './interfaces/inventory-report';
import {
    each as _each,
    trim as _trim,
    toInteger as _toInteger,
    toNumber as _toNumber,
    concat as _concat,
    sortBy as _sortBy,
} from 'lodash';

@Injectable({
    providedIn: 'root',
})
export class InventoryXlsxReaderService extends BaseRequester {
    START_PRODUCT_ROW = 8;

    async read(file: any): Promise<InventoryReport> {
        const workbook = new Workbook();
        const wb: Workbook = await workbook.xlsx.load(file);
        const inventoryWs: Worksheet | undefined = wb.getWorksheet(
            'Inventory Warehouse',
        );

        if (inventoryWs) {
            this.validate(inventoryWs);
            const report: InventoryReport = {
                companyName: inventoryWs.getCell('B2').value!.toString(),
                taxId: inventoryWs.getCell('B3').value!.toString(),
                inventoryItems: [],
            };
            for (
                let i = this.START_PRODUCT_ROW;
                i < inventoryWs.lastRow!.number;
                i++
            ) {
                const row = inventoryWs.getRow(i);
                if (row) {
                    const rowValues = row.values;
                    if (rowValues.length === 8) {
                        const rv = rowValues as any;
                        const orderNo = _toInteger(rv[1]);
                        const productName = _trim(rv[2]);
                        const sku = _trim(rv[3]);
                        const avgPrice = _toNumber(rv[4]);
                        const onHand = _toInteger(rv[5]);
                        const unit = _trim(rv[6]);
                        const value = _toNumber(rv[7]);
                        const item: InventoryItem = {
                            orderNo,
                            productName,
                            sku,
                            avgPrice,
                            onHand,
                            unit,
                            value,
                        };
                        report.inventoryItems = _concat(
                            report.inventoryItems,
                            item,
                        );
                    }
                } else {
                    throw new Error(
                        `Invalid row data at row ${i}. Please check the file for inconsistencies.`,
                    );
                }
            }
            report.inventoryItems = _sortBy(report.inventoryItems, [
                'onHand',
                'sku',
            ]);
            return report;
        }
        throw new Error(
            "Worksheet 'Inventory Warehouse' is missing or could not be processed.",
        );
    }

    private validate(inventoryWs: Worksheet): void {
        const checkList = {
            A2: 'ชื่อบริษัท',
            A3: 'เลขผู้เสียภาษี :',
            A4: 'ณ วันที่ :',
            A5: 'คลังสินค้า',
            A7: 'ลำดับที่',
            B7: 'ชื่อสินค้า',
            C7: 'รหัสสินค้า',
            D7: 'ราคาสินค้าคงเหลือเฉลี่ยต่อหน่วย',
            E7: 'จำนวนคงคลัง',
            F7: 'หน่วยสินค้าหลัก',
            G7: 'มูลค่า',
        };

        _each(checkList, (val, key) => {
            let actualValue: string = inventoryWs
                .getCell(key)
                .value!.toString();
            actualValue = _trim(actualValue);
            if (actualValue !== val) {
                throw new Error(
                    `Validation failed at cell ${key}: expected "${val}", but got "${actualValue}"`,
                );
            }
        });
    }
}
